import { useCallback, useState } from 'react';
import type { RefObject } from 'react';

const nonNegativeClamp = (value: number) => Math.max(0, value);

export type UseOverflowBoxParam<ElementType extends HTMLElement> = {
  overflowBoxRef: RefObject<ElementType>;
};

export const useOverflowBox = <ElementType extends HTMLElement>({
  overflowBoxRef,
}: UseOverflowBoxParam<ElementType>) => {
  const overflowBoxElement = overflowBoxRef?.current;

  const [overflowBox, setOverflowBox] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const updateOverflowBox = useCallback(() => {
    if (overflowBoxElement) {
      setOverflowBox({
        top: nonNegativeClamp(Math.floor(overflowBoxElement.scrollTop)),
        right: nonNegativeClamp(
          Math.floor(
            overflowBoxElement.scrollWidth -
              overflowBoxElement.clientWidth -
              overflowBoxElement.scrollLeft,
          ),
        ),
        bottom: nonNegativeClamp(
          Math.floor(
            overflowBoxElement.scrollHeight -
              overflowBoxElement.clientHeight -
              overflowBoxElement.scrollTop,
          ),
        ),
        left: nonNegativeClamp(Math.floor(overflowBoxElement.scrollLeft)),
      });
    }
  }, [overflowBoxElement, setOverflowBox]);

  const hasOverflow =
    overflowBox.top ||
    overflowBox.bottom ||
    overflowBox.left ||
    overflowBox.right;

  return {
    overflowBoxRef,
    updateOverflowBox,
    hasOverflow,
    overflowBox,
  };
};
